import {
    getCurrencyForLocale,
    normalizeLocaleCode,
} from '@/util/currencyFormat'
import {
    CURRENCIES,
    DEFAULT_PREFS,
    CurrencyPrefs,
} from '@/feature/admin/Environment/utils/types'

export interface EnvironmentSetting {
    key: string
    value: any
}

export interface CurrencyOverrideSettings {
    display?: CurrencyPrefs['displayAs']
    display_position?: 'before' | 'after'
    decimal_separator?: string
    thousands_separator?: string
    decimal_places?: number
}

export type CurrencyOverrides = Required<CurrencyOverrideSettings>

const getSettingValue = (
    settings: EnvironmentSetting[],
    key: string
): unknown => settings.find((setting) => setting.key === key)?.value

const getStringSettingValue = (
    settings: EnvironmentSetting[],
    key: string
): string | undefined => {
    const value = getSettingValue(settings, key)
    if (typeof value !== 'string') return undefined
    return value || undefined
}

const parseCurrencyOverrides = (
    value: unknown
): CurrencyOverrideSettings | undefined => {
    try {
        const parsed: CurrencyOverrideSettings =
            typeof value === 'string'
                ? JSON.parse(value)
                : ((value ?? {}) as CurrencyOverrideSettings)

        return parsed
    } catch {
        return undefined
    }
}

/**
 * Builds a complete CurrencyOverrides object for a given currency code,
 * applying defaults from CURRENCIES + DEFAULT_PREFS, then layering any
 * backend overrides on top.
 */
export function buildCurrencyOverrides(
    code: string,
    backendOverrides?: CurrencyOverrideSettings
): CurrencyOverrides {
    const known = CURRENCIES.find((c) => c.code === code)
    const decimals = known?.decimals ?? DEFAULT_PREFS.decimals

    return {
        display_position: backendOverrides?.display_position ?? 'before',
        decimal_separator:
            backendOverrides?.decimal_separator ??
            DEFAULT_PREFS.decimalSeparator,
        thousands_separator:
            backendOverrides?.thousands_separator ??
            DEFAULT_PREFS.thousandsSeparator,
        decimal_places: backendOverrides?.decimal_places ?? decimals,
        display: backendOverrides?.display ?? 'symbol',
    }
}

export function resolveCurrencyCode(
    settingsData: EnvironmentSetting[],
    locale: string
): string {
    return (
        getStringSettingValue(settingsData, 'currency') ??
        getStringSettingValue(settingsData, 'valuta') ??
        getCurrencyForLocale(locale) ??
        DEFAULT_PREFS.code
    )
}

export function resolveLocale(
    settingsData: EnvironmentSetting[],
    kedo: any
): string {
    const env = kedo?.env?.()
    return (
        normalizeLocaleCode(
            getStringSettingValue(settingsData, 'locale') ??
                env?.getSelectedLocale?.()?.code ??
                kedo?.getLanguage?.()
        ) ?? DEFAULT_PREFS.locale
    )
}

export function syncEnvironmentCurrencySettings(
    settingsData: EnvironmentSetting[],
    kedo: any
): void {
    const env = kedo?.env?.()
    if (!env) return

    const locale = resolveLocale(settingsData, kedo)
    const code = resolveCurrencyCode(settingsData, locale)
    const backendOverrides = parseCurrencyOverrides(
        getSettingValue(settingsData, 'currency_overrides')
    )

    env.currency = code
    env.currencyOverrides = buildCurrencyOverrides(code, backendOverrides)
}

export function syncEnvironmentSettings(
    settingsData: EnvironmentSetting[],
    kedo: any
): void {
    syncEnvironmentCurrencySettings(settingsData, kedo)
}

export async function loadEnvironmentSettings(kedo: any): Promise<void> {
    try {
        const env = kedo?.env?.()
        const envId = env?.getEnvironmentId?.()
        if (!env || !envId) return

        const api = kedo.api()
        const response = await api.get(api.getSettingEndpoint(envId))
        const settingsData: EnvironmentSetting[] = Array.isArray(
            response?.data?.results
        )
            ? response.data.results
            : []
        syncEnvironmentSettings(settingsData, kedo)
    } catch (err) {
        console.error('Failed to load environment settings', err)
    }
}
